import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Collapse,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  Grass as GrassIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import {
  buildContractedServiceItems,
  normalizeCheckedServices,
} from '../../utils/contractedServiceItems';
import ContractedServicesChecklist from './ContractedServicesChecklist';

/**
 * Summer Job Details Component
 * Displays summer-specific logging fields for lawn care and landscaping jobs
 * Includes: Services Performed (selected from available services), General Notes
 */
const SummerJobDetails = ({ job, onUpdate }) => {
  const { t } = useTranslation();
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    services: true,
    notes: true
  });

  // Parse available and checked services from job
  const availableServices = job.available_services
    ? (typeof job.available_services === 'string' ? JSON.parse(job.available_services) : job.available_services)
    : {};

  const [checkedServices, setCheckedServices] = useState(() => (
    normalizeCheckedServices(job.checked_services)
  ));

  const [jobNotes, setJobNotes] = useState(job.notes || '');
  const [datedNotes, setDatedNotes] = useState(job.messages || []);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Auto-save with debounce
  const debouncedSave = React.useCallback(
    (() => {
      let timeoutId;
      return (saveFunction) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(saveFunction, 1000);
      };
    })(),
    []
  );

  // Shared save helper. Surfaces backend errors to the user — without this
  // a 500 would disappear silently and the crew would assume the save worked.
  const patchJson = async (body, successKey) => {
    try {
      setSaving(true);
      const response = await makeApiCall(`api/jobs/${job.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const msg = errData.error || t('summerLogs.saveFailedGeneric');
        console.error('Summer-log save failed:', msg);
        setErrorMessage(msg);
        return false;
      }
      setSaveMessage(t(successKey));
      setTimeout(() => setSaveMessage(''), 2000);
      return true;
    } catch (error) {
      console.error('Summer-log save network error:', error);
      setErrorMessage(error.message || t('summerLogs.saveFailedGeneric'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveCheckedServices = (updatedServices) =>
    patchJson({ checked_services: JSON.stringify(updatedServices) }, 'summerLogs.servicesSaved');

  const saveJobNotes = (notes) => patchJson({ notes }, 'summerLogs.notesSaved');

  const addDatedNote = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      const response = await makeApiCall(`api/jobs/${job.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_content: trimmed,
          message_type: 'standard'
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setErrorMessage(errData.error || t('summerLogs.saveFailedGeneric'));
        return;
      }

      const data = await response.json();
      setDatedNotes((prev) => [data.job_message, ...prev]);
      setNewNote('');
      setSaveMessage(t('summerLogs.notesSaved'));
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error adding dated summer note:', error);
      setErrorMessage(error.message || t('summerLogs.saveFailedGeneric'));
    } finally {
      setSaving(false);
    }
  };

  // Handle service checkbox change
  const handleServiceChange = (serviceKey) => (event) => {
    const updated = { ...checkedServices, [serviceKey]: event.target.checked };
    setCheckedServices(updated);
    debouncedSave(() => saveCheckedServices(updated));
  };

  const toggleService = (serviceKey) => {
    handleServiceChange(serviceKey)({
      target: { checked: !checkedServices[serviceKey] },
    });
  };

  // Handle notes change
  const handleNotesChange = (event) => {
    const notes = event.target.value;
    setJobNotes(notes);
    debouncedSave(() => saveJobNotes(notes));
  };

  const serviceItems = buildContractedServiceItems(availableServices, {
    t,
    customerType: job.customer_type,
  });

  return (
    <Box sx={{ mt: 3 }}>
      {/* Save success indicator - Snackbar at bottom */}
      <Snackbar
        open={!!saveMessage}
        message={saveMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={2000}
      />

      {/* Save error indicator - sticky until dismissed */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErrorMessage('')}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorMessage('')}
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Services Performed */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <GrassIcon sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6">{t('summerLogs.servicesPerformed')}</Typography>
            </Box>
            <IconButton onClick={() => toggleSection('services')} size="small">
              <ExpandIcon 
                sx={{ 
                  transform: expandedSections.services ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} 
              />
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.services}>
            <Box sx={{ mt: 2 }}>
              <ContractedServicesChecklist
                items={serviceItems}
                values={checkedServices}
                onToggle={toggleService}
                emptyMessage={t('summerLogs.noServicesAvailable')}
              />
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* General Notes */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('summerLogs.generalNotes')}</Typography>
            <IconButton onClick={() => toggleSection('notes')} size="small">
              <ExpandIcon 
                sx={{ 
                  transform: expandedSections.notes ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }} 
              />
            </IconButton>
          </Box>
          
          <Collapse in={expandedSections.notes}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('summerLogs.addDatedNote', 'Add Dated Note')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={t('summerLogs.datedNotePlaceholder', 'Add a note. The date and author will be saved automatically.')}
                value={newNote}
                onChange={(event) => setNewNote(event.target.value)}
                inputProps={{ 'aria-label': t('summerLogs.addDatedNote', 'Add dated note') }}
              />
              <Box sx={{ mt: 1, mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addDatedNote}
                  disabled={saving || !newNote.trim()}
                  aria-label={t('summerLogs.addDatedNote', 'Add dated note')}
                >
                  {t('jobs.addMessage', 'Add Message')}
                </Button>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                {t('summerLogs.datedNotes', 'Dated Notes')}
              </Typography>
              {datedNotes.length > 0 ? (
                <List dense sx={{ mb: 3 }}>
                  {datedNotes.map((message) => (
                    <ListItem key={message.id} divider disableGutters>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" gap={2}>
                            <Typography variant="body2" fontWeight="medium">
                              {message.sender_name || message.sender_username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {message.created_at ? new Date(message.created_at).toLocaleString() : ''}
                            </Typography>
                          </Box>
                        }
                        secondary={(
                          <>
                            {message.message_content}
                            {message.is_admin_only && (
                              <Chip label={t('jobs.adminOnly')} color="warning" size="small" sx={{ ml: 1 }} />
                            )}
                          </>
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('summerLogs.noDatedNotes', 'No dated notes yet.')}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('summerLogs.legacyGeneralNotes', 'Legacy General Notes')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('summerLogs.legacyGeneralNotesHelp', 'Existing free-text notes are preserved below. Use dated notes above for new entries.')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={t('summerLogs.notesPlaceholder')}
                value={jobNotes}
                onChange={handleNotesChange}
              />
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Info Card showing contracted services from the quote (parent categories only) */}
      {serviceItems.filter(i => i.type === 'service').length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'info.lighter' }}>
          <CardContent>
            <Typography variant="subtitle2" color="info.dark" gutterBottom>
              {t('summerLogs.contractedServices')}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {serviceItems
                .filter(i => i.type === 'service')
                .map((item) => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SummerJobDetails;

